import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, IVideoGeneration, VideoClipStatus, VideoEditConfig } from '@packages/global';
import { VideoCameraIcon, XMarkIcon, ArrowUpTrayIcon, FilmIcon, TrashIcon } from '@heroicons/react/24/outline';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { useModels } from '../../../hooks/useModels';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import VideoSelector from '../../common/VideoSelector';

interface VideoTabProps {
  videoClip: IVideoClip;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  editConfig?: VideoEditConfig | null;
  onEditConfigChange?: (config: VideoEditConfig) => void;
  onRequestClose?: () => void;
}

export default function VideoTab({ videoClip, refreshVideoClip, editConfig, onEditConfigChange, onRequestClose }: VideoTabProps) {
  const { t } = useTranslation(['editor', 'common']);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideoSelector, setShowVideoSelector] = useState(false);

  const { models } = useModels();
  const { alert, confirm } = useDialog();
  const { success, error: showError } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // 当 videoClip 变化时，重新加载视频
  useEffect(() => {
    if (videoRef.current && videoClip.videoUrl) {
      videoRef.current.load();
    }
  }, [videoClip.id, videoClip.videoUrl]);

  // 获取视频时长
  const getVideoDuration = (videoUrl: string): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration);
      };
      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };
      video.src = videoUrl;
    });
  };

  const updateEditConfigForReplacement = (newVideoDuration: number) => {
    if (!editConfig || !onEditConfigChange) return;

    const clipIndex = editConfig.clips.findIndex(c => c.clipId === videoClip.id);
    if (clipIndex === -1) return;

    const updatedClips = [...editConfig.clips];
    const currentClip = updatedClips[clipIndex];
    const currentTrimStart = currentClip.trimStart ?? 0;
    const currentTrimEnd = currentClip.trimEnd ?? (currentTrimStart + currentClip.duration);
    const currentUsedDuration = Math.max(0, currentTrimEnd - currentTrimStart) || currentClip.duration;

    let nextTrimStart = currentTrimStart;
    let nextTrimEnd = currentTrimEnd;
    let nextDuration = currentUsedDuration;

    // 新视频足够长时，优先保留原裁剪区间；保留不了就至少保留原有效时长。
    if (newVideoDuration >= currentTrimEnd) {
      nextTrimStart = currentTrimStart;
      nextTrimEnd = currentTrimEnd;
      nextDuration = currentUsedDuration;
    } else if (newVideoDuration >= currentUsedDuration) {
      nextDuration = currentUsedDuration;
      nextTrimStart = Math.min(currentTrimStart, newVideoDuration - currentUsedDuration);
      nextTrimStart = Math.max(0, nextTrimStart);
      nextTrimEnd = nextTrimStart + currentUsedDuration;
    } else {
      nextTrimStart = 0;
      nextTrimEnd = newVideoDuration;
      nextDuration = newVideoDuration;
    }

    updatedClips[clipIndex] = {
      ...currentClip,
      duration: nextDuration,
      trimStart: nextTrimStart,
      trimEnd: nextTrimEnd
    };

    for (let i = clipIndex + 1; i < updatedClips.length; i++) {
      updatedClips[i] = {
        ...updatedClips[i],
        startTime: updatedClips[i - 1].startTime + updatedClips[i - 1].duration
      };
    }

    const totalDuration = updatedClips.reduce((sum, clip) => sum + clip.duration, 0);

    onEditConfigChange({
      ...editConfig,
      clips: updatedClips,
      timeline: {
        ...editConfig.timeline,
        totalDuration
      }
    });
  };

  const handleUploadVideo = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await fetch('/api/user/upload/video', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const result = await response.json();
      if (!result.success || !result.data?.url) {
        throw new Error(result.message || 'Upload failed');
      }

      const videoUrl = result.data.url;
      const duration = await getVideoDuration(videoUrl);

      await videoClipApi.updateVideoClip(videoClip.id, {
        videoUrl,
        duration,
        status: VideoClipStatus.COMPLETED
      });

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }

      updateEditConfigForReplacement(duration);

      success(t('editor:videoTab.videoUploadSuccess'));
    } catch (err: any) {
      console.error('上传视频失败:', err);
      showError(err.message || t('editor:common.videoApplyFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearVideo = async () => {
    const confirmed = await confirm(t('editor:videoTab.clearVideoConfirm'), t('editor:imagesTab.confirmClear'), 'warning');
    if (!confirmed) return;

    try {
      await videoClipApi.updateVideoClip(videoClip.id, { videoUrl: '' });

      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
      success(t('editor:videoTab.videoCleared'));
    } catch (err: any) {
      console.error('清除视频失败:', err);
      showError(t('editor:common.videoApplyFailed'));
    }
  };

  const handleSelectVideo = async (video: IVideoGeneration) => {
    try {
      const videoUrl = video.resultVideoUrl;
      if (!videoUrl) {
        alert(t('editor:common.videoApplyFailed'));
        return;
      }

      // 获取视频时长
      const duration = await getVideoDuration(videoUrl);

      // 更新 VideoClip
      await videoClipApi.updateVideoClip(videoClip.id, {
        videoUrl,
        duration,
        status: VideoClipStatus.COMPLETED
      });

      // 先刷新 videoClip 数据，确保时间线能获取到最新的视频
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }

      updateEditConfigForReplacement(duration);

      setShowVideoSelector(false);
      success(t('editor:videoTab.videoApplied'));
    } catch (error) {
      console.error('选择视频失败:', error);
      alert(t('editor:common.videoApplyFailed'));
    }
  };

  return (
    <>
      <div className="flex flex-col h-full max-h-[90vh] bg-bg-surface text-gray-50">
        {/* 隐藏的文件输入 */}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadVideo(f); }}
          className="hidden"
        />
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border-subtle">
          <div className="flex items-baseline gap-3 min-w-0">
            <h2 className="text-lg font-semibold shrink-0">{t('editor:previewArea.video')}</h2>
            <p className="text-sm text-gray-400 truncate">{videoClip.title || t('editor:videoTab.noVideo')}</p>
          </div>
          <button
            onClick={onRequestClose}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border-subtle bg-bg-elevated hover:bg-border-default text-gray-200 shrink-0"
            title={t('common:close', { defaultValue: '关闭' })}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
            <div className="rounded-2xl border border-border-subtle bg-bg-form p-4">
              <div className="aspect-video rounded-xl overflow-hidden bg-black flex items-center justify-center">
                {videoClip.videoUrl ? (
                  <video
                    ref={videoRef}
                    className="w-full h-full object-contain"
                    controls
                    key={videoClip.id}
                  >
                    <source src={videoClip.videoUrl} type="video/mp4" />
                  </video>
                ) : (
                  <div className="text-center px-6">
                    <FilmIcon className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                    <p className="text-sm text-gray-400">{t('editor:videoTab.noVideo')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
                <h3 className="text-sm font-medium text-gray-300 mb-4">{t('editor:videoTab.selectVideo')}</h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowVideoSelector(true)}
                    className="px-4 py-2 bg-bg-elevated hover:bg-border-default text-gray-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <VideoCameraIcon className="w-4 h-4" />
                    {t('common:select')}
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    {uploading ? t('editor:videoTab.uploading') : t('common:upload')}
                  </button>
                </div>
                {videoClip.status === 'video_generation' && (
                  <p className="text-sm text-orange-400 mt-3">{t('editor:videoTab.videoGenerating')}</p>
                )}
              </div>

              {videoClip.videoUrl && (
                <div className="rounded-2xl border border-red-900/40 bg-red-950/15 p-5">
                  <button
                    onClick={handleClearVideo}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm rounded-xl transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {t('common:clear')}
                  </button>
                  <p className="mt-2 text-xs leading-5 text-gray-400">
                    {t('editor:videoTab.clearVideoConfirm')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <VideoSelector
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={handleSelectVideo}
        models={models}
      />
    </>
  );
}
